import React from 'react';
import { Home as HomeIcon, Camera, BellRing, Smartphone, Lightbulb, Thermometer, Car, KeyRound } from 'lucide-react';
import { CTAButton, PageHero, SectionLabel } from '../components/shared';
const features = [{
  icon: BellRing,
  title: 'Smart alarms',
  text: 'Door, window, motion, and glass-break sensors with optional 24/7 monitoring.'
}, {
  icon: Camera,
  title: 'Cameras and doorbells',
  text: 'See who is at the door, in the driveway, or in the backyard — day or night.'
}, {
  icon: KeyRound,
  title: 'Smart locks',
  text: 'Lock and unlock from anywhere. Custom codes for guests, kids, and cleaners.'
}, {
  icon: Car,
  title: 'Garage control',
  text: 'Open, close, and get alerts if the garage is left open. Tied to your alarm system.'
}, {
  icon: Lightbulb,
  title: 'Lighting and scenes',
  text: 'Schedule lights, link them to your security system, and never come home to a dark house.'
}, {
  icon: Thermometer,
  title: 'Climate and water',
  text: 'Smart thermostats, water sensors, and energy automation that pays you back.'
}];
export function SmartHome() {
  return <>
      <PageHero eyebrow="Residential stays strong" title="Smart home security from the same local team." subtitle="Powered by Alarm.com — alarms, cameras, locks, lights, garage, and climate, all in one app, supported by a real Northern California team." data-id="element-475">
        <div className="flex flex-wrap gap-3" data-id="element-476">
          <CTAButton to="/assessment" data-id="element-477">Free home assessment</CTAButton>
          <CTAButton variant="secondary" href="https://www.alarm.com/US/watchdogsmarthome?home=1" data-id="element-478">
            Customer login
          </CTAButton>
        </div>
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-id="element-479">
        <SectionLabel data-id="element-480">What is included</SectionLabel>
        <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-481">
          One app for the whole home.
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-482">
          {features.map(f => {
          const Icon = f.icon;
          return <div key={f.title} className="rounded-[2rem] border border-slate-200 bg-slate-50 p-7" data-id="element-483">
                <Icon className="mb-5 h-7 w-7 text-sky-500" data-id="element-484" />
                <h3 className="text-xl font-black" data-id="element-485">{f.title}</h3>
                <p className="mt-3 leading-7 text-slate-600" data-id="element-486">{f.text}</p>
              </div>;
        })}
        </div>
      </section>

      <section className="bg-white py-20" data-id="element-487">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[.95fr_1.05fr] lg:px-8 lg:items-center" data-id="element-488">
          <div className="rounded-[2rem] bg-gradient-to-br from-sky-100 via-white to-slate-100 p-6 shadow-xl ring-1 ring-slate-200" data-id="element-489">
            <div className="rounded-[1.5rem] bg-white p-6 shadow-sm" data-id="element-490">
              <div className="mb-6 flex items-center justify-between" data-id="element-491">
                <div data-id="element-492">
                  <p className="text-sm font-black text-slate-950" data-id="element-493">
                    Smart Home Dashboard
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-494">
                    Protected by Watchdog
                  </p>
                </div>
                <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-700" data-id="element-495">
                  Armed Stay
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-496">
                {([[HomeIcon, 'Front Door', 'Locked'], [Camera, 'Driveway', 'Live'], [BellRing, 'Alarm', 'Ready'], [Smartphone, 'Mobile App', 'Connected']] as const).map(([Icon, title, status]) => <div key={title} className="rounded-3xl bg-slate-50 p-5 ring-1 ring-slate-200" data-id="element-497">
                    <Icon className="mb-5 h-7 w-7 text-sky-500" data-id="element-498" />
                    <p className="font-black" data-id="element-499">{title}</p>
                    <p className="mt-1 text-sm text-slate-500" data-id="element-500">{status}</p>
                  </div>)}
              </div>
            </div>
          </div>
          <div data-id="element-501">
            <SectionLabel data-id="element-502">Why homeowners pick Watchdog</SectionLabel>
            <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-503">
              Real people. Real installs. Real support.
            </h2>
            <p className="mt-6 text-lg leading-8 text-slate-600" data-id="element-504">
              No call centers in another country. We design your system, install
              it cleanly, walk you through how to use it, and stay available
              when you need us.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-505">
              <CTAButton to="/assessment" data-id="element-506">Schedule a home consult</CTAButton>
              <CTAButton variant="dark" href="tel:+19168424729" data-id="element-507">
                Call (916) 842-4729
              </CTAButton>
            </div>
          </div>
        </div>
      </section>
    </>;
}